/**
 * SiteQuickActionButtons Component
 * Context-aware action buttons based on site status and current phase
 */

import React, { useState, useMemo } from 'react';
import { SITE_STATUS, SITE_PHASE_STATUS } from '../../utils/constants';
import { hasDirectorAccess } from '../../utils/rolePermissions';
import BottomSheet from '../common/BottomSheet';

// Priority order for sorting actions
const PRIORITY_ORDER = { primary: 0, secondary: 1, tertiary: 2 };

// Category definitions for grouping actions in bottom sheet
const ACTION_CATEGORIES = {
  team: {
    label: 'Team',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )
  },
  status: {
    label: 'Status',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  phase: {
    label: 'Phase Actions',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    )
  }
};

// Categorize action based on id
const categorizeAction = (action) => {
  const id = action.id?.toLowerCase() || '';

  if (id.includes('assign')) return 'team';
  if (id.includes('resume') || id.includes('hold') || id.includes('completed')) return 'status';
  return 'phase'; // start, complete, skip, progress, add
};

// Get softer background color classes for action icons in bottom sheet
const getIconBgClasses = (color) => {
  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-100 text-red-600'
  };
  return colorMap[color] || colorMap.blue;
};

const SiteQuickActionButtons = ({
  site,
  phases = [],
  userRole,
  onStartPhase,
  onCompletePhase,
  onSkipPhase,
  onUpdateProgress,
  onAddPhase,
  onChangeStatus,
  onAssignTeam,
  isMobileFAB = false,
  // Attendance props for mobile FAB
  attendanceStatus = null,
  onCheckIn = null,
  onCheckOut = null
}) => {
  const [loading, setLoading] = useState(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);

  // Find current phase (first in_progress or first pending)
  const currentPhase = phases.find(p => p.status === SITE_PHASE_STATUS.IN_PROGRESS) ||
    phases.find(p => p.status === SITE_PHASE_STATUS.PENDING);

  // Determine available actions based on site status and current phase
  const getAvailableActions = () => {
    const actions = [];
    const siteStatus = site?.siteStatus;

    // If site is pending SS assignment, show assign team action
    if (siteStatus === SITE_STATUS.PENDING_SS_ASSIGNMENT) {
      actions.push({
        id: 'assign_team',
        label: 'Assign Site Team',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        onClick: onAssignTeam,
        color: 'orange',
        priority: 'primary',
        roles: ['director', 'manager', 'site_manager', 'operation_head']
      });
      return actions;
    }

    // If site is on hold, only show resume action
    if (siteStatus === SITE_STATUS.ON_HOLD) {
      actions.push({
        id: 'resume_site',
        label: 'Resume Site',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => onChangeStatus && onChangeStatus(SITE_STATUS.ACTIVE),
        color: 'green',
        priority: 'primary',
        roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
      });
      return actions;
    }

    // If site is completed, no additional actions needed (history shown on page)
    if (siteStatus === SITE_STATUS.COMPLETED) {
      return actions;
    }

    // Active site actions
    if (currentPhase) {
      // If there's a phase in progress
      if (currentPhase.status === SITE_PHASE_STATUS.IN_PROGRESS) {
        actions.push({
          id: 'update_progress',
          label: 'Update Progress',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          ),
          onClick: () => onUpdateProgress && onUpdateProgress(currentPhase.id),
          color: 'blue',
          priority: 'primary',
          roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
        });

        actions.push({
          id: 'complete_phase',
          label: `Complete ${currentPhase.name}`,
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          onClick: () => onCompletePhase && onCompletePhase(currentPhase.id),
          color: 'green',
          priority: 'primary',
          roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
        });
      } else {
        // Phase is pending - offer to start it
        actions.push({
          id: 'start_phase',
          label: `Start ${currentPhase.name}`,
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          onClick: () => onStartPhase && onStartPhase(currentPhase.id),
          color: 'blue',
          priority: 'primary',
          roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
        });

        actions.push({
          id: 'skip_phase',
          label: `Skip ${currentPhase.name}`,
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          ),
          onClick: () => onSkipPhase && onSkipPhase(currentPhase.id),
          color: 'yellow',
          priority: 'secondary',
          roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
        });
      }
    }

    // Add phase action (always available for active sites)
    actions.push({
      id: 'add_phase',
      label: 'Add Phase',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      onClick: onAddPhase,
      color: 'purple',
      priority: 'tertiary',
      roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
    });

    // Put on hold action
    actions.push({
      id: 'put_on_hold',
      label: 'Put On Hold',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => onChangeStatus && onChangeStatus(SITE_STATUS.ON_HOLD),
      color: 'gray',
      priority: 'secondary',
      roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
    });

    // Mark as completed (only if all phases are done or skipped)
    const allPhasesComplete = phases.every(
      p => p.status === SITE_PHASE_STATUS.COMPLETED || p.status === SITE_PHASE_STATUS.SKIPPED
    );
    if (allPhasesComplete && phases.length > 0) {
      actions.push({
        id: 'mark_completed',
        label: 'Mark Completed',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => onChangeStatus && onChangeStatus(SITE_STATUS.COMPLETED),
        color: 'green',
        priority: 'primary',
        roles: ['director', 'manager', 'site_manager', 'site_supervisor', 'operation_head']
      });
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  // Filter by role - check for director equivalents when 'director' is in roles
  const filteredActions = availableActions.filter(action => {
    if (action.roles.includes('all')) return true;
    if (action.roles.includes(userRole?.toLowerCase())) return true;
    // If action allows 'director', also allow director-equivalent roles
    if (action.roles.includes('director') && hasDirectorAccess(userRole)) return true;
    return false;
  });

  // Color classes
  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-600 hover:bg-blue-700 text-white',
      green: 'bg-green-600 hover:bg-green-700 text-white',
      yellow: 'bg-yellow-500 hover:bg-yellow-600 text-white',
      orange: 'bg-orange-600 hover:bg-orange-700 text-white',
      purple: 'bg-purple-600 hover:bg-purple-700 text-white',
      indigo: 'bg-indigo-600 hover:bg-indigo-700 text-white',
      gray: 'bg-gray-500 hover:bg-gray-600 text-white',
      red: 'bg-red-600 hover:bg-red-700 text-white'
    };
    return colorMap[color] || colorMap.blue;
  };

  const handleAction = async (action) => {
    if (!action.onClick) return;

    setLoading(action.id);
    try {
      await action.onClick();
    } catch (error) {
      console.error(`Error executing action ${action.id}:`, error);
    } finally {
      setLoading(null);
    }
  };

  // Group actions by category for mobile bottom sheet
  const groupedActions = useMemo(() => {
    const groups = {};
    filteredActions.forEach(action => {
      const category = categorizeAction(action);
      if (!groups[category]) groups[category] = [];
      groups[category].push(action);
    });
    return groups;
  }, [filteredActions]);

  // Sort actions by priority and split into quick (top 3) and more actions
  const { quickActions, moreActions } = useMemo(() => {
    const sorted = [...filteredActions].sort(
      (a, b) => (PRIORITY_ORDER[a.priority] || 1) - (PRIORITY_ORDER[b.priority] || 1)
    );
    return {
      quickActions: sorted.slice(0, 3),
      moreActions: sorted.slice(3)
    };
  }, [filteredActions]);

  // Define category display order
  const categoryOrder = ['phase', 'status', 'team'];

  if (filteredActions.length === 0) {
    return null;
  }

  // Determine contextual primary action
  const getPrimaryFABAction = () => {
    // Priority: Check Out > Check In > Top priority action
    if (attendanceStatus?.canCheckOut && onCheckOut) {
      return {
        id: 'check_out',
        label: 'Check Out',
        onClick: onCheckOut,
        color: 'red',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        )
      };
    }
    if (attendanceStatus?.canCheckIn && onCheckIn) {
      return {
        id: 'check_in',
        label: 'Check In',
        onClick: onCheckIn,
        color: 'green',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
        )
      };
    }
    // Return top priority action if available
    if (quickActions.length > 0) {
      return quickActions[0];
    }
    return null;
  };

  const primaryFABAction = isMobileFAB ? getPrimaryFABAction() : null;

  // Get FAB background color
  const getFABBgColor = (color) => {
    const colorMap = {
      blue: 'bg-blue-600 hover:bg-blue-700',
      green: 'bg-green-600 hover:bg-green-700',
      yellow: 'bg-yellow-500 hover:bg-yellow-600',
      orange: 'bg-orange-600 hover:bg-orange-700',
      purple: 'bg-purple-600 hover:bg-purple-700',
      red: 'bg-red-600 hover:bg-red-700',
      gray: 'bg-gray-600 hover:bg-gray-700'
    };
    return colorMap[color] || colorMap.blue;
  };

  // Mobile FAB render
  if (isMobileFAB) {
    // Determine attendance FAB action
    const attendanceFABAction = attendanceStatus?.canCheckOut && onCheckOut
      ? {
          id: 'check_out',
          label: 'Check Out',
          onClick: onCheckOut,
          color: 'red',
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          )
        }
      : attendanceStatus?.canCheckIn && onCheckIn
        ? {
            id: 'check_in',
            label: 'Check In',
            onClick: onCheckIn,
            color: 'green',
            icon: (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            )
          }
        : null;

    return (
      <>
        {/* Mobile: Attendance FAB (primary) + Lightning FAB (secondary) */}
        <div className="fixed bottom-6 right-6 z-40 flex flex-col-reverse items-center gap-3">
          {/* Primary FAB - Check In/Out if available, otherwise Lightning */}
          {attendanceFABAction ? (
            <button
              onClick={() => handleAction(attendanceFABAction)}
              disabled={loading === attendanceFABAction.id}
              className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 touch-manipulation active:scale-95 ${getFABBgColor(attendanceFABAction.color)}`}
              title={attendanceFABAction.label}
            >
              {loading === attendanceFABAction.id ? (
                <svg className="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <span className="w-6 h-6 [&>svg]:w-6 [&>svg]:h-6 text-white">{attendanceFABAction.icon}</span>
              )}
            </button>
          ) : (
            <button
              onClick={() => setBottomSheetOpen(true)}
              className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 touch-manipulation active:scale-95 bg-blue-600 hover:bg-blue-700"
              title="Quick Actions"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </button>
          )}

          {/* Secondary FAB - Lightning for quick actions (only show if attendance FAB is primary) */}
          {attendanceFABAction && filteredActions.length > 0 && (
            <button
              onClick={() => setBottomSheetOpen(true)}
              className="w-11 h-11 rounded-full shadow-md flex items-center justify-center transition-all duration-200 touch-manipulation active:scale-95 bg-blue-600 hover:bg-blue-700"
              title="Quick Actions"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </button>
          )}
        </div>

        {/* Bottom Sheet with quick access grid + more actions */}
        <BottomSheet
          isOpen={bottomSheetOpen}
          onClose={() => {
            setBottomSheetOpen(false);
            setShowMoreActions(false);
          }}
          title="Quick Actions"
        >
          <div className="pb-4">
            {/* Quick Access Grid - Top 3 priority actions */}
            {quickActions.length > 0 && (
              <div className="px-4 py-3">
                <div className="grid grid-cols-3 gap-3">
                  {quickActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => {
                        handleAction(action);
                        setBottomSheetOpen(false);
                      }}
                      disabled={loading === action.id}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation disabled:opacity-50"
                    >
                      <span className={`w-12 h-12 rounded-full flex items-center justify-center ${getIconBgClasses(action.color)}`}>
                        {loading === action.id ? (
                          <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <span className="w-6 h-6 [&>svg]:w-6 [&>svg]:h-6">{action.icon}</span>
                        )}
                      </span>
                      <span className="text-xs font-medium text-gray-700 text-center leading-tight line-clamp-2">
                        {action.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* More Actions - Collapsible section */}
            {moreActions.length > 0 && (
              <div className="border-t border-gray-100">
                <button
                  onClick={() => setShowMoreActions(!showMoreActions)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left active:bg-gray-50 touch-manipulation"
                >
                  <span className="text-sm font-medium text-gray-600">
                    More Actions ({moreActions.length})
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${showMoreActions ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Collapsible content */}
                <div className={`overflow-hidden transition-all duration-200 ${showMoreActions ? 'max-h-96' : 'max-h-0'}`}>
                  {moreActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => {
                        handleAction(action);
                        setBottomSheetOpen(false);
                      }}
                      disabled={loading === action.id}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors active:bg-gray-50 touch-manipulation min-h-[52px] border-t border-gray-50 disabled:opacity-50"
                    >
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getIconBgClasses(action.color)}`}>
                        {loading === action.id ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">{action.icon}</span>
                        )}
                      </span>
                      <span className="text-sm text-gray-700">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </BottomSheet>
      </>
    );
  }

  // Get priority-based styling
  const getPriorityClasses = (priority) => {
    const priorityMap = {
      primary: 'ring-2 ring-offset-1 ring-current/20 shadow-md',    // Prominent
      secondary: 'shadow-sm',                                        // Normal
      tertiary: 'opacity-80 text-xs'                                 // De-emphasized
    };
    return priorityMap[priority] || priorityMap.secondary;
  };

  // Desktop: Compact toolbar
  return (
    <div className="flex flex-wrap items-center gap-2">
      {filteredActions.map((action) => (
        <button
          key={action.id}
          onClick={() => handleAction(action)}
          disabled={loading === action.id}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${getColorClasses(action.color)} ${getPriorityClasses(action.priority)}`}
          title={action.label}
        >
          {loading === action.id ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">{action.icon}</span>
          )}
          <span className="hidden sm:inline">{action.label}</span>
        </button>
      ))}
    </div>
  );
};

export default SiteQuickActionButtons;
